// Ruach mode — Hebrew scripture + New Testament Logos theology
// Every scientific concept mapped to its ancient counterpart

export const RUACH_UI = {
  appTitle: 'Ruach Model',
  appSubtitle: 'The Breath That Gives Form',
  fieldName: 'Ruach Elohim',
  fieldHebrew: 'רוּחַ אֱלֹהִים',
  fieldDescription: 'The Spirit of God that moved over the formless void, breathing substance and structure into all created things.',
  centerName: 'The Breath',
  centerHebrew: 'רוּחַ',
  centerSymbol: 'א',
  couplingLabel: 'Breath imparted',
  masslessLabel: 'Pure light — untouched by form',
  massLabel: 'Weight of being',
  sectionMass: 'Weight of being',
  sectionForces: 'The four pillars',
  sectionOrbital: 'Breath & form',
  timelimeLabel: 'From Tohu va-Bohu to now',
  timelineHebrew: 'תֹהוּ וָבֹהוּ',
  beyondLabel: 'Ein Sof — what lies beyond',
  factTitle: 'Ancient witness',
  toggleHint: 'Switch to science mode',
};

export const RUACH_FACTS = [
  { label: 'Source', value: 'Ruach Elohim · רוּחַ אֱלֹהִים' },
  { label: 'Genesis 1:2', value: '"...and the Spirit of God was hovering over the waters."' },
  { label: 'John 1:3', value: '"Through him all things were made."' },
  { label: 'Forms given', value: '9 of 12 fermions' },
  { label: 'Force carriers formed', value: 'W±, Z⁰' },
  { label: 'Untouched by form', value: 'Light (γ), Gluon (g)' },
  { label: 'Proton weight from Ruach', value: '~1%' },
  { label: 'Proton weight from binding', value: '~99%' },
];

export const RUACH_FORCES = [
  {
    name: 'Light — Or (אוֹר)',
    carrier: 'Photon — pure light',
    range: 'Infinite',
    ruach: 'none',
    ruachNote: 'Untouched by the Ruach — remains pure, formless, infinite',
    color: '#ffffff',
    scripture: '"And God said, Let there be light." — Genesis 1:3 · "In him was life, and that life was the light of all mankind." — John 1:4',
    detail: 'The photon carries no mass because it does not interact with the Ruach field. It remains as it was at creation — pure, unbounded, traveling at the speed of light. This is why light fills all space without limit.',
    acts: 'All charged, formed things',
  },
  {
    name: 'The Weak Breath',
    carrier: 'W± and Z⁰',
    range: 'Within the nucleus only',
    ruach: 'full',
    ruachNote: 'W & Z receive great weight from the Ruach — confined to small places',
    color: '#ff9040',
    scripture: '"He set the boundaries of the peoples." — Deuteronomy 32:8 · The Word that limits and defines.',
    detail: 'The Ruach gives the W and Z particles enormous weight — and that weight confines their reach to distances far smaller than a single proton. This is the force that governs radioactive decay and the burning of stars.',
    acts: 'All formed matter (fermions)',
  },
  {
    name: 'The Strong Binding',
    carrier: 'Gluon — the bond',
    range: 'Within the proton only',
    ruach: 'none',
    ruachNote: 'Gluons receive no Ruach mass — yet are bound by color confinement',
    color: '#e060ff',
    scripture: '"He is before all things, and in him all things hold together." — Colossians 1:17',
    detail: 'The gluon is massless — untouched by the Ruach field — yet the strong force it carries is confined within hadrons by color confinement. The energy of this binding is what gives the proton nearly all of its mass. Matter is held together not by the Ruach directly, but by the energy of confinement.',
    acts: 'Quarks (color charge)',
  },
  {
    name: 'Gravity — The Great Drawing',
    carrier: 'Unknown — beyond our model',
    range: 'Infinite',
    ruach: 'outside',
    ruachNote: 'Beyond the Standard Model — beyond current understanding',
    color: 'rgba(255,255,255,0.3)',
    scripture: '"Where were you when I laid the earth\'s foundation?" — Job 38:4 · The deep mystery that holds galaxies together.',
    detail: 'Gravity remains outside the Standard Model entirely. No quantum theory of gravity yet exists. Like Ein Sof — the infinite that lies beyond — gravity reminds us that our current model is not the final word.',
    acts: 'All energy and mass',
  },
];

export const RUACH_PARTICLES = {
  // QUARKS
  top: {
    ruachName: 'The Heaviest Word',
    ruachHebrew: 'דָּבָר כָּבֵד',
    description: 'The heaviest known elementary particle — given the greatest weight by the Ruach. So unstable it dissolves almost instantly, like a word spoken and immediately returned to breath. "By the word of the LORD the heavens were made." — Psalm 33:6',
    detail: 'Its enormous weight comes from the strongest coupling to the Ruach field of any fermion. It decays before it can form bound structures — a reminder that not all things formed are meant to persist.',
    couplingNote: 'Strongest Ruach coupling — fullest imparting of weight',
  },
  bottom: {
    ruachName: 'The Deep Foundation',
    ruachHebrew: 'יְסוֹד',
    description: 'The 3rd generation quark that anchors B-mesons — particles used to study why the universe contains more matter than antimatter. At the heart of one of creation\'s deepest asymmetries. "He laid the foundations of the earth." — Psalm 104:5',
    detail: 'H→bb̄ is the most common way the Higgs boson dissolves — returning its weight to the most common heavy quark pair. The most frequent return to foundation.',
    couplingNote: 'Strong Ruach coupling',
  },
  charm: {
    ruachName: 'The Hidden Grace',
    ruachHebrew: 'חֵן נִסְתָּר',
    description: 'The 2nd generation quark, carrying a quantum number called "charm" — an unexpected delight discovered in 1974 in the November Revolution of physics. "Every good and perfect gift is from above." — James 1:17',
    detail: 'Charmed hadrons travel measurable distances before dissolving — a brief journey that leaves a detectable trace.',
    couplingNote: 'Moderate Ruach coupling',
  },
  strange: {
    ruachName: 'The Strange Gift',
    ruachHebrew: 'מַתָּנָה זָרָה',
    description: 'Named for "strangeness" — a quantum number that behaves unexpectedly in weak decays. Creation contains things that do not follow simple rules. "For my thoughts are not your thoughts." — Isaiah 55:8',
    detail: 'Strange quarks bubble in and out of existence inside protons as virtual pairs — present but invisible, like breath held beneath the surface.',
    couplingNote: 'Weak Ruach coupling',
  },
  up: {
    ruachName: 'The First Utterance',
    ruachHebrew: 'אוֹת רִאשׁוֹן',
    description: 'One of the two quarks inside every proton (uud). Given very little weight by the Ruach — yet together with the down quark, forms the building block of all visible matter. "The LORD God formed the man from the dust." — Genesis 2:7',
    detail: 'Despite its tiny Ruach mass, the up quark participates in the strong binding that gives the proton nearly all its weight. Most of what we are comes not from the Ruach directly, but from the energy of binding.',
    couplingNote: 'Faintest Ruach coupling — yet essential to all matter',
  },
  down: {
    ruachName: 'The Second Utterance',
    ruachHebrew: 'אוֹת שֵׁנִי',
    description: 'Slightly heavier than the up quark. Two down quarks in the neutron make it heavier than the proton — enabling beta decay, which powers nuclear reactions in stars, which made the elements that became us. "He determines the number of the stars and calls them each by name." — Psalm 147:4',
    detail: 'Beta decay converts a down quark to an up quark — the weak force at work, governed by the W boson, whose weight comes from the Ruach.',
    couplingNote: 'Faint Ruach coupling',
  },
  // LEPTONS
  tau: {
    ruachName: 'The Brief Witness',
    ruachHebrew: 'עֵד קָצָר',
    description: 'Heaviest charged lepton — lives only 3×10⁻¹³ seconds. It comes into being and is gone before anything can form around it. A witness to the Ruach\'s imparting, immediately returned. "Man is like a breath; his days are like a fleeting shadow." — Psalm 144:4',
    detail: 'H→ττ was one of the first direct measurements confirming the Ruach gives weight to leptons as well as quarks.',
    couplingNote: 'Strong Ruach coupling for a lepton',
  },
  muon: {
    ruachName: 'The Wandering Breath',
    ruachHebrew: 'רוּחַ נוֹדֶדֶת',
    description: '"Who ordered that?" Physicist I.I. Rabi said when the muon was discovered — a heavier copy of the electron with no obvious purpose in creation. Yet it exists. Cosmic ray muons pass through your body constantly. "The wind blows wherever it pleases." — John 3:8',
    detail: 'The muon\'s anomalous magnetic moment shows a persistent discrepancy with our model — a possible whisper of something beyond current understanding.',
    couplingNote: 'Moderate Ruach coupling',
  },
  electron: {
    ruachName: 'The Living Spark',
    ruachHebrew: 'נִיצוֹץ חַיִּים',
    description: 'The lightest charged particle — the first confirmed to receive weight from the Ruach field. Forms the shells of atoms, enables chemistry, carries electricity, emits light. Without the electron, no chemistry, no life, no thought. "The LORD God breathed into his nostrils the breath of life." — Genesis 2:7',
    detail: 'The electron\'s Ruach-given mass sets the size of atoms via the Bohr radius. A heavier electron would shrink all atoms; lighter would expand them — all chemistry would be unrecognizable.',
    couplingNote: 'Faintest charged lepton Ruach coupling — yet life depends on it',
  },
  nu_tau: {
    ruachName: 'The Hidden Breath',
    ruachHebrew: 'נְשִׁימָה נִסְתֶּרֶת',
    description: 'Billions of neutrinos pass through your body every second — through walls, through the earth — without touching anything. They are almost entirely unformed, almost pure breath. "The wind blows wherever it pleases. You hear its sound, but you cannot tell where it comes from or where it is going." — John 3:8',
    detail: 'Neutrino mass is one of the genuine mysteries beyond our current model — they clearly have some weight, but how they received it from the Ruach field (if at all) remains unknown.',
    couplingNote: 'Mass uncertain — beyond current understanding',
  },
  nu_mu: {
    ruachName: 'The Second Hidden Breath',
    ruachHebrew: 'נְשִׁימָה שְׁנִיָּה',
    description: 'Confirmed in 1962 as distinct from the electron neutrino. The discovery that neutrino flavors are separate was itself a revelation — creation contains distinctions too subtle for the eye to see. "For now we see only a reflection as in a mirror." — 1 Corinthians 13:12',
    detail: 'Atmospheric neutrino oscillations in 1998 proved neutrinos change identity as they travel — requiring non-zero mass, and pointing to physics beyond our model.',
    couplingNote: 'Mass uncertain — beyond current understanding',
  },
  nu_e: {
    ruachName: 'The First Hidden Breath',
    ruachHebrew: 'נְשִׁימָה רִאשׁוֹנָה',
    description: 'Proposed by Pauli in 1930 to explain energy that seemed to vanish in beta decay — energy that was never lost, only hidden in an invisible particle. "Nothing in all creation is hidden from God\'s sight." — Hebrews 4:13',
    detail: 'Solar neutrinos produced in the Sun\'s core take 100,000 years to escape by light, but reach us in 8 minutes as neutrinos — passing through the entire Sun as if it were not there.',
    couplingNote: 'Mass uncertain — beyond current understanding',
  },
  // BOSONS
  w: {
    ruachName: 'The Boundary Setter',
    ruachHebrew: 'שׁוֹמֵר הַגְּבוּל',
    description: 'The W boson carries the weak force — and receives enormous weight from the Ruach field. This weight confines its reach to distances far smaller than a proton. It is the force behind beta decay, stellar fusion, and ultimately the light of every star. "He set the boundary it cannot cross." — Psalm 104:9',
    detail: 'Without the Ruach giving the W boson its mass, the weak and electromagnetic forces would be unified — as they were in the early universe, before the Ruach field was established.',
    couplingNote: 'Very strong Ruach coupling — second only to Z',
  },
  z: {
    ruachName: 'The Neutral Boundary',
    ruachHebrew: 'גְּבוּל נֶאֱמָן',
    description: 'The Z boson is the neutral carrier of the weak force — heavier even than the W. It also receives its weight from the Ruach. Like the W, it is confined. "He marks out the horizon on the face of the waters for a boundary between light and darkness." — Job 26:10',
    detail: 'The Z boson mass is one of the most precisely measured quantities in physics — a fixed point around which the entire electroweak structure is anchored.',
    couplingNote: 'Strongest boson Ruach coupling',
  },
  photon: {
    ruachName: 'The Or (אוֹר) — Pure Light',
    ruachHebrew: 'אוֹר',
    description: '"And God said, Let there be light — וַיְהִי אוֹר." — Genesis 1:3. The photon is massless because it does not interact with the Ruach field. It was the first thing spoken into being — pure, unbounded, infinite in range. "In him was life, and that life was the light of all mankind." — John 1:4',
    detail: 'The photon\'s masslessness is protected by gauge symmetry — a deep mathematical principle that ensures light remains untouched through all of electroweak symmetry breaking. Creation\'s first utterance remains uncorrupted.',
    couplingNote: 'No Ruach coupling — pure light, as first created',
  },
  gluon: {
    ruachName: 'The Hidden Bond',
    ruachHebrew: 'קֶשֶׁר נִסְתָּר',
    description: 'The gluon binds quarks inside protons with a force so strong that quarks can never be separated. Massless — untouched by the Ruach directly — yet the energy of its binding is what gives protons nearly all their weight. "In him all things hold together." — Colossians 1:17',
    detail: 'The confinement energy of gluons binding quarks is responsible for ~99% of the mass of all visible matter. Most of what you are — most of what exists — comes not from the Ruach field directly, but from this hidden binding energy.',
    couplingNote: 'No Ruach coupling — yet its binding creates most visible mass',
  },
  higgs: {
    ruachName: 'The Ruach Elohim — רוּחַ אֱלֹהִים',
    ruachHebrew: 'רוּחַ אֱלֹהִים',
    description: '"The earth was formless and empty — תֹהוּ וָבֹהוּ — and the Spirit of God was hovering over the waters." — Genesis 1:2. The Higgs boson is the detectable ripple in the Ruach field — the moment when the invisible breath becomes measurable. Discovered at CERN in 2012.',
    detail: '"In the beginning was the Word, and the Word was with God, and the Word was God... Through him all things were made; without him nothing was made that has been made." — John 1:1,3. The Ruach field is not the Higgs boson — the field is the breath, the boson is a ripple in it. The field exists everywhere; the boson is what we detect when we disturb it.',
    couplingNote: 'The source — the field itself made momentarily visible',
  },
};

export const MODE_COMPARISON = [
  { science: 'Higgs field', ruach: 'Ruach Elohim · רוּחַ אֱלֹהִים', scripture: 'Genesis 1:2' },
  { science: 'Higgs boson (H⁰)', ruach: 'The Breath made visible · אוֹת', scripture: 'John 1:14 — "The Word became flesh"' },
  { science: 'Coupling strength', ruach: 'Degree of Ruach imparted', scripture: '' },
  { science: 'Mass', ruach: 'Weight of being', scripture: '' },
  { science: 'Massless particles (γ, g)', ruach: 'Pure light — untouched by form', scripture: 'Genesis 1:3 — "Let there be light"' },
  { science: 'W & Z gain mass', ruach: 'Given weight and boundary', scripture: 'Deuteronomy 32:8 — "He set the boundaries"' },
  { science: 'Big Bang', ruach: 'Tohu va-Bohu · תֹהוּ וָבֹהוּ', scripture: 'Genesis 1:2 — "formless and empty"' },
  { science: 'Electroweak symmetry breaking', ruach: 'The moment form entered the void', scripture: 'Genesis 1:3 — first utterance' },
  { science: 'Particle decay', ruach: 'Return to source', scripture: 'Ecclesiastes 12:7 — "the spirit returns to God"' },
  { science: 'Beyond Standard Model', ruach: 'Ein Sof — what lies beyond knowing', scripture: 'Job 38:4 — "Where were you when I laid the foundation?"' },
  { science: 'Proton mass from QCD', ruach: 'Weight from the hidden binding', scripture: 'Colossians 1:17 — "in him all things hold together"' },
  { science: 'Neutrino mass unknown', ruach: 'The breath whose weight we cannot measure', scripture: 'John 3:8 — "you cannot tell where it comes from"' },
];
